import { useCallback, useEffect, useState } from "react";
import { CloseIcon } from "./icons";
import { getRemarks, saveRemark } from "../api/leadApi";
import { isApiSuccess } from "../utils/apiResponse";

// created_date comes as "YYYY-MM-DD HH:mm:ss" — swap the space for "T" so
// Safari (iOS webview) parses it too. Shown as "30 Sep · 04:14 PM".
const formatRemarkTime = (value) => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d)) return "";
  const day = d.toLocaleDateString("en-PH", { day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: true });
  return day + " · " + time;
};

const TAB_LABELS = { sent_financier: "Send to Bank", noa_received: "NOA Approval", contract_signed_details: "Contract Signing" };
// Unknown tab_name values fall back to a title-cased version ("some_tab" → "Some Tab").
const tabLabel = (tab) => TAB_LABELS[tab] || String(tab || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Remark history for one lead, shared by every post-application stage.
export function useRemarks(leadId, flash) {
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!leadId) return Promise.resolve();
    setLoading(true);
    return getRemarks(leadId)
      .then(({ data }) => {
        if (isApiSuccess(data)) setRemarks(Array.isArray(data.data) ? data.data : []);
        else console.error("get_remarks returned", data?.status, data?.message);
      })
      .catch((err) => console.error("get_remarks failed", err.response?.status, err.response?.data || err.message))
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => {
    setRemarks([]);
    load();
  }, [load]);

  const add = async (tabName, remark) => {
    setSaving(true);
    try {
      const { data } = await saveRemark(leadId, tabName, remark);
      if (!isApiSuccess(data)) {
        flash?.(data?.message || "Could not save remark");
        return false;
      }
      flash?.(data.message || "Remark saved");
      await load();
      return true;
    } catch (err) {
      console.error("save_remark failed", err.response?.status, err.response?.data || err.message);
      flash?.(err.response?.data?.message || "Could not save remark");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { remarks, loading, saving, add, reload: load };
}

// get_remarks gives date ("26 Oct 2026") / time ("03:45 PM") / status_name /
// description; older responses only had created_date / tab_name / remark.
const entryTime = (r) => (r.date && r.time ? r.date + " · " + r.time : formatRemarkTime(r.created_date));
const entryStatus = (r) => r.status_name || (r.tab_name ? tabLabel(r.tab_name) : "");

export default function RemarksModal({ open, remarks, loading, saving, onSubmit, onClose, readOnly = false }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!open) setText("");
  }, [open]);
  if (!open) return null;

  const submit = async () => {
    const remark = text.trim();
    if (!remark || saving) return;
    if (await onSubmit(remark)) setText("");
  };

  return (
    <div className="overlay overlay--mail" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mail-box remarks-box">
        <div className="remarks-box__head">
          <span className="remarks-box__title">Remark History</span>
          <button type="button" className="sheet-box__close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="remarks-box__list">
          {loading && remarks.length === 0 && <div className="history-empty">Loading remarks…</div>}
          {!loading && remarks.length === 0 && <div className="history-empty">No remarks yet.</div>}
          {remarks.map((r) => (
            <div key={r.id} className="remarks-entry">
              <div className="remarks-entry__head">
                <span className="remarks-entry__time">
                  {entryTime(r)}
                  {r.created_by_name && " · " + r.created_by_name}
                </span>
                {entryStatus(r) && <span className="remarks-entry__tab">{entryStatus(r)}</span>}
              </div>
              <div className="remarks-entry__text">{r.description ?? r.remark}</div>
              {r.financier_name && <div className="remarks-entry__bank">{r.financier_name}</div>}
            </div>
          ))}
        </div>
        {!readOnly && (
        <div className="remarks-box__foot">
          <span className="remarks-box__label">Add Remark</span>
          <textarea
            className="mail-box__textarea remarks-box__textarea"
            placeholder="Add Remark"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="button" className="btn-cta remarks-box__submit" onClick={submit} disabled={!text.trim() || saving}>
            {saving ? "Submitting…" : "Submit"}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
